/** @jsx tsx */
import { v } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import FroalaEditor from "froala-editor";
import $ from "jquery";
import * as _ from "lodash";
import { getFroalaSelectionSpanStyle } from "../../../csi/service/fontService";
import { HIGHLIGHT_ID } from "../../Constants";
import { eventHandler } from "../../store/EventHandler";
import { firstCharacterStyles } from "../../store/reducers/util";
import { IExtents } from "../interfaces/model/dataStructures/index";
import { Keys } from "../interfaces/model/enumerations/index";
import { getSelectedPageId, stateConstants } from "../selectors/index";

const _FroalaEditor: any = FroalaEditor;

/**
 * Toolbar options interface
 */
export interface IToolbarOptions {
	toolbarSticky?: boolean;
	toolbarVisibleWithoutSelection?: boolean;
	toolbarInline?: boolean;
	toolbarContainer?: string;
}

/**
 * Froala editor config object
 */
export interface IFroalaEditorOptions {
	[key: string]: any;
}

/**
 * Froala widget props interface
 */
export interface IFroalaEditorProps {
	/** Content for the editor. */
	content?: string;

	/** Toolbar options. */
	toolbarType: IToolbarOptions;

	/**
	 * Froala edit on/off flag
	 */
	editOn?: boolean;

	/**
	 * Actual height of text area inside the shape
	 */
	textRectHeight: number;

	/**
	 * ShapeId for selected shape
	 */
	shapeId: string;

	/**
	 * Actual spPr.xfrm width of shape
	 */
	shapeWidth?: number;

	/**
	 * Key for shape group
	 */
	groupShapeKey?: string;

	/** Page id of the selected page */
	selectedPageId: string;

	/**
	 * Flag to indicate whether the shape is text shape or not
	 */
	isTextShape?: boolean;

	/** Selected page number */
	selectedPageNumber: number;

	/** First character styles for selected shape */
	firstCharStyle: any;

	/** Migration extents for the selected shape */
	migrationExtents?: IExtents;

	/** Callback for content change handling. */
	onContentChanged?(content: string, selectedPageNumber: number, transient?: boolean): void;

	/** Callback to set text editor instance. */
	setTextEditorInstance(textEditorInstance: any): void;

	/** Callback to set first char styles */
	setFormatPainterTextStyles(textStyles: any, valueStyles: any): void;

	/** Set selected shape extends */
	setObjectExtends(extend: { cx?: number; cy?: number }, transient?: boolean): void;

	/** Deselect all objects callback function */
	deselectAllObjects?(): void;

	/**
	 * Select object id callback
	 * @param selectedObject Object index in its parent child list
	 * @param isMultiSelect Flag to indicate multi selection
	 * @param transient Undo/redo stack flag
	 * @param forceSingleSelect Flag to indicate whether or not to force single select
	 */
	selectObjectById?(selectionId: string, isMultiSelect: boolean, transient: boolean, forceSingleSelect: boolean): void;

	/**
	 * Select object callback
	 * @param selectedObject Object index in its parent child list
	 * @param isMultiSelect Flag to indicate multi selection
	 * @param transient Undo/redo stack flag
	 * @param childId optionally select a subgroup element if selecting a group
	 */
	selectObject?(selectedObject: number, isMultiSelect?: boolean, transient?: boolean, childId?: number): void;

	/** Callback to set text first character styles */
	setFirstCharStyle(textStyles: any): void;

	/** Remove migrated extents callback function */
	removeMigratedExtents?(shapeIds: string[]): void;

	/** Textbox resizer callback for observer */
	textboxResizer(mutationsList: any, observer: MutationObserver): void;
}

/**
 * Froala editor widget
 */
export class FroalaEditorWrapper extends WidgetBase<IFroalaEditorProps> {
	// Jquery wrapped element.
	private $element: any;

	// Froala editor object, in which we will get all froala editor API's
	private editor: any;

	// Froala editor initialize check
	private editorInitialized: boolean = false;

	// Froala element
	private $editor: JQuery | null = null;

	// Represents current selected page id while froala editor initialize
	private currentPageId: string = "";

	// Froala editor event listeners
	private listeningEvents: string[] = [];

	// Observer for FroalaEditor.el
	private observer?: MutationObserver;

	// Froala editor configuration
	private config: IFroalaEditorOptions = {
		charCounterCount: false,

		// TODO - "fa" is no longer used by Font Awesome; instead it is "fas", "far", etc.
		htmlAllowedEmptyTags: ["textarea", "a", "iframe", "object", "video", "style", "script", ".fa"],

		imagePaste: false,
		keepFormatOnDelete: true,
		linkAlwaysBlank: true,
		linkEditButtons: ["linkOpen", "linkEdit", "linkRemove"],
		linkInsertButtons: [],
		linkList: null,
		paragraphFormat: {
			H1: "Heading 1",
			H2: "Heading 2",
			H3: "Heading 3",
			H4: "Heading 4",
			N: "Normal",
		},
		pasteAllowedStyleProps: ["font-size", "font-family", "text-align", "margin-left", "color"],
		pasteDeniedAttrs: ["class", "id"],
		pasteDeniedTags: ["img"],
		placeholderText: "",
		pluginsEnabled: ["align", "colors", "fontFamily", "fontSize", "inlineStyle",
			"lists", "paragraphFormat", "paragraphStyle", "wordPaste", "link", "table"],
		tableEditButtons: ["tableRows", "tableColumns", "tableCells"],
		tabSpaces: 4,
		// Customized froala theme "csi-froala-theme.styl" has been added in the C8,
		// c8-canvas library will consume the same theme from C8.
		theme: "csi-froala",
		toolbarInline: true,
		wordPasteModal: false,
	};

	/**
	 * Gets the text node
	 * @return {HTMLElement}
	 */
	protected getTextNode() {
		return $(this.editor.$el[0])[0];
	}

	// Return current selection color
	protected _getSelectionColor(editor: any) {
		const element = editor.selection.element();
		const elementStyle = element.style;
		const computedStyle = window.getComputedStyle(element);
		for (const prop in elementStyle) {
			if (prop === "color") {
				return editor.helpers.RGBToHex(computedStyle[prop]);
			}
		}
	}

	/**
	 * Widget onAttach life cycle hook, call after render
	 */
	protected onAttach() {
		this.initializeEditor();
	}

	/**
	 * Clean the froala HTML when use the spell check plugin to correct the spelling and remove
	 * empty paragraph tags which has only br tags
	 */
	protected cleanFroalaHTML(htmlStr: string) {
		const textNode = document.createElement("div");
		textNode.innerHTML = htmlStr;
		const paragraphs = textNode.querySelectorAll("p");

		const fontTags = textNode.querySelectorAll("font");
		if (fontTags.length) {
			_.each(fontTags, (font: HTMLElement) => {
				const span = document.createElement("span");
				const color = font.getAttribute("color");
				const fontFamily = font.getAttribute("face");
				span.innerHTML = this.getCleanHTML(font.innerHTML);
				span.style.color = color || "";
				span.style.fontFamily = fontFamily || "";
				font.outerHTML = span.outerHTML;
			});
		} else {
			textNode.innerHTML = this.getCleanHTML(textNode.innerHTML);
		}
		return textNode.innerHTML;
	}

	/**
	 * Return clean HTML as froala needed, replace b, i and strike tags with
	 * froala supported tags such as strong, em, and s tags respectively.
	 * @param value {string}
	 */
	protected getCleanHTML(value: string): string {
		return (value || "").replace("<b>", "<strong>")
			.replace("</b>", "</strong>").
			replace("<i>", "<em>")
			.replace("</i>", "</em>").
			replace("<strike>", "<s>")
			.replace("</strike>", "</s>");
	}

	/**
	 * Widget detach life cycle hook, call before destroy widget
	 */
	protected onDetach() {
		if (this.properties.selectedPageId === getSelectedPageId()) {
			this.updateTextContent(false);
		}

		// reset shape text styles in state on froala detach event
		this.properties.setFirstCharStyle({});

		// disconnest observer
		if (this.observer) {
			this.observer.disconnect();
		}

		this.destroyEditor();
	}

	/**
	 * Update text content in state
	 * @param transient {boolean}
	 */
	protected updateTextContent(transient: boolean) {
		const textNode = this.getTextNode();
		const newContent = this.cleanFroalaHTML(textNode.innerHTML);
		const {
			content,
			onContentChanged,
			selectedPageNumber,
		} = this.properties;
		// Change the text content only if the original text change or user typing text
		const isContentSame = (content === newContent);
		if (onContentChanged && (!isContentSame || transient)) {
			onContentChanged(newContent, selectedPageNumber, transient);
		}
	}

	/**
	 * Initialize froala editor.
	 */
	protected initializeEditor() {
		if (this.editorInitialized) {
			return;
		}

		const {
			content,
			editOn,
			selectedPageId,
			setTextEditorInstance,
			shapeId,
			textboxResizer,
			toolbarType,
		} = this.properties;

		this.currentPageId = selectedPageId;
		this.config = { ...this.config, ...toolbarType, key: stateConstants().license };
		this.$element = $(`#${shapeId}`);
		this.editor = new _FroalaEditor($(this.$element)[0], this.config, () => {
			const { editor } = this;

			// editor is created so now use it
			this.$editor = editor.$el as JQuery;
			editor.events.focus();
			this.initFroalaListeners();
			this.editorInitialized = true;
			if (!editOn) {
				editor.edit.off();
			}

			const textNode = this.getTextNode();

			// Initalize observer to watch for changes in textNode for autosize.
			const options = {
				characterData: true,
				subtree: true,
			};

			let { observer } = this;
			observer = new MutationObserver(textboxResizer);
			observer.observe(textNode, options);

			// Set caret at first td if table shape, or at end if not table
			editor.selection.setAtEnd(this.getElement("last"));
			editor.selection.restore();
			// Set froala editor instance in state
			setTextEditorInstance(editor);

			// Call this on initialization as to not wait for onKeyUp
			this.handleEditorEvents({});
		});
	}

	/**
	 * Get the element in text content to set the caret at last.
	 * Example:- <div class="fr-element fr-view">
	 *              <p style="text-align: left;">
	 *                  <span style="font-family: Arial; font-size: 12px; color: rgb(0, 0, 0);">Hello World</span>
	 *              </p>
	 *               <p style="text-align: left;">
	 *                  <span style="font-family: Arial; font-size: 12px; color: rgb(0, 0, 0);">
	 *                      <strong><s><u>How are you?</u></s></strong>
	 *                  </span>
	 *              </p>
	 *          </div>
	 * getElement("first") will return:- <span>Hello World</span>
	 * getElement("last") will return:- <u>How are you?</u>
	 */
	protected getElement(elementPosition: string) {
		// getting first or last paragraph in froala editor
		let currentElement;
		if (elementPosition === "first") {
			currentElement = this.editor.$el.get(0).firstChild;
		} else {
			currentElement = this.editor.$el.get(0).lastChild;
		}
		let element;
		// extracting first or last element in currentElement which has actual text
		while (currentElement) {
			if (currentElement.nodeName !== "#text") {
				element = currentElement;
			}
			if (elementPosition === "first") {
				currentElement = currentElement.firstChild || currentElement.lastChild as HTMLElement;
			} else {
				currentElement = currentElement.lastChild || currentElement.firstChild as HTMLElement;
			}
		}
		return element;
	}

	/**
	 * Destroy froala instance and event listeners
	 */
	protected destroyEditor() {
		if (this.$element && this.$editor) {
			this.$element.off(this.listeningEvents.join(" "));
			this.$editor.off("keyup");
			this.editor.destroy();
			this.editor = null;
			this.listeningEvents.length = 0;
			this.$element = null;
			this.editorInitialized = false;
			this.properties.setTextEditorInstance(null);
			this.currentPageId = "";
		}
	}

	/**
	 * Check if everything is selected inside the editor. Froala editor has selection.isFull() method however it
	 * is always returning false so implementing customize method which return's whether full text is selected or not.
	 * @return {boolean}
	 */
	protected isFullTextSelected() {
		const rangeInfo: { atStart: boolean; atEnd: boolean } = this.editor.selection.info(this.editor.$el.get(0));
		return rangeInfo.atStart && rangeInfo.atEnd;
	}

	/**
	 * Return's current selection font size.
	 * @return {boolean}
	 */
	protected getSelectionFontSize() {
		let fontSize: string = "";
		if (this.isFullTextSelected() && !this.editor.browser.chrome) {
			// while full text is selected inside the editor editor.selection.element() return's first span in editor
			// while in other browsers it return full editor instance or paragraph so in this
			// case getting first span inside editor
			const firstSpan = this.editor.$el.find("span")[0];
			fontSize = $(firstSpan)
				.css("font-size");
		} else {
			fontSize = $(this.editor.selection.element())
				.css("font-size");
		}
		return this.editor.helpers.getPX(fontSize) + "px";
	}

	/**
	 * Key down event for when inside Editor
	 * If no span element is found, reset to default styles.
	 * Default styles are:
	 * <p style="text-align: left;">
	 *  <span style="color: rgb(0, 0, 0); font-size: 12px; font-family: Arial;">&8203;</span>
	 * </p>
	 * Using editor.format.applyStyle adds the span back for us.
	 * @param e {KeyboardEvent}
	 */
	protected onKeyDown(e: KeyboardEvent) {
		const { editor } = this;
		const spanEl = editor.el.querySelector("span");
		if (!spanEl) {
			editor.align.apply("left");
			editor.format.applyStyle("font-size", "12px");
			editor.format.applyStyle("font-family", "Arial");
			editor.colors.text("#000");
		}
	}

	/** disable dragging of editor contents */
	protected dragStart(e: DragEvent) {
		e.preventDefault();
	}

	/**
	 * Bind contentChange and keyup froala events
	 */
	protected initFroalaListeners() {
		const { editor } = this;

		this.registerEvent(editor, "dragstart", this.dragStart.bind(this));
		this.registerEvent(editor, "keydown", this.onKeyDown.bind(this));
		this.registerEvent(editor, "keyup", this.handleEditorEvents.bind(this));
		this.registerEvent(editor, "click", this.handleEditorEvents.bind(this));
		this.registerEvent(editor, "contentChanged", this.handleEditorEvents.bind(this));
		this.registerEvent(editor, "table.resized", this.handleEditorEvents.bind(this));

		this.registerEvent(editor, "commands.before", (cmd) => {
			if (cmd === "bold") {
				const { format } = editor;
				const isBold = parseInt(getFroalaSelectionSpanStyle("font-weight") || "0") >= 500;
				if (isBold) {
					format.applyStyle("font-weight", "400");
				} else {
					format.applyStyle("font-weight", "700");
				}
				this.handleEditorEvents({});
				return false;
			}

			if (cmd === "italic") {
				const { format } = editor;
				const isItalic = getFroalaSelectionSpanStyle("font-style") === cmd;
				if (isItalic) {
					format.applyStyle("font-style", "normal");
				} else {
					format.applyStyle("font-style", "italic");
				}
				this.handleEditorEvents({});
				return false;
			}
		});

		// When insert link popup is closed, remove colored span
		editor.popups.onHide("link.insert", (() => {
			editor.selection.restore();
			$("#" + HIGHLIGHT_ID)
				.remove();
		}).bind(this));
	}

	/**
	 * Handle events for Keyup and Click.
	 * @param e {Event}
	 * @param editor {Editor} Editor instance
	 */
	protected handleEditorEvents(e: KeyboardEvent | any) {
		const {
			deselectAllObjects,
			groupShapeKey,
			selectedPageId,
			selectObject,
			selectObjectById,
			setFirstCharStyle,
			shapeId,
		} = this.properties;
		const { editor } = this;

		// This object is used to map our toolbar plugins values with froala current selection
		const _textStyles: any[] = [{ name: "strong", value: "strong" }, { name: "em", value: "italic" },
			{ name: "u", value: "underline" }, { name: "s", value: "strikethrough" },
			{ name: "ol", value: "ol" }, { name: "ul", value: "ul" }];

		// froala default paragraph styles
		const _paragraphStyles: any[] = ["n", "h1", "h2", "h3", "h4"];
		const textAttrs: any = {};

		// Current selection alignment
		const blocks = editor.selection.blocks();
		if (blocks.length) {
			const alignment = editor.helpers.getAlignment($(blocks[0]));
			textAttrs[alignment] = alignment;
		} else {
			textAttrs.left = "left";
		}

		// get the selection style like bold, italic, underline and strikethrough
		_.each(_textStyles, (element) => {
			if (editor.format.is(element.name)) {
				textAttrs[element.value] = element.name;
			}
		});

		// Current selection color style
		textAttrs.color = this._getSelectionColor(editor);

		// Current selection font size
		textAttrs["font-size"] = this.getSelectionFontSize();

		// get the selection paragraph style
		textAttrs.paragraph = _paragraphStyles[0];
		_.each(_paragraphStyles, (style) => {
			if (editor.format.is(style)) {
				// tslint:disable-next-line:no-string-literal
				textAttrs["paragraph"] = style;
			}
		});

		// get current selection
		const currentSelection: HTMLElement = editor.selection.element();

		// get first char styles
		const firstCharStyles = firstCharacterStyles(editor.el.innerHTML);

		// Example: froala using '\'Times New Roman\',Times,serif' as default for the Times New Roman
		// So '\'Times New Roman\',Times,serif'.replace(/"/g,'\'').replace(/, /g, ',')
		// will be return "'Times New Roman',Times,serif"
		// finally fontFamily.replace(/[']+/g, ''); will return "Times New Roman,Times,serif"
		const fontFamily = $(currentSelection)
			.css("font-family")
			.replace(/"/g, "\'")
			.replace(/, /g, ",");

		// if the current selection is the entire editor box, use first char styles instead of the current selection
		const { classList } = currentSelection;
		if (classList.contains("fr-element") && classList.contains("fr-view")) {
			textAttrs["font-family"] = firstCharStyles["font-family"];
		} else {
			textAttrs["font-family"] = fontFamily.replace(/[']+/g, "");
		}

		// Emit update toolbar event to C8
		if (eventHandler) {
			eventHandler.emit("onTextSelectionChange", textAttrs);
		}

		// Set first character styles
		setFirstCharStyle(firstCharStyles);

		// On ESCAPE key press get out of text edit mode
		if (e?.keyCode === Keys.ESCAPE && deselectAllObjects) {
			// first deselect all objects to get out of text edit mode
			deselectAllObjects();
			// now find out if we are in a group
			if (!groupShapeKey && selectObjectById) {
				// not in a group, single select a normal object
				selectObjectById(shapeId, false, true, true);
			} else if (selectObject) {
				// in a group, select the subgroup when hitting escape
				if (selectObject && groupShapeKey) {
					// in order to select a subgroup, you must select the group first
					const groupKey = groupShapeKey.split("_");
					const parentId = parseInt(groupKey[0]);
					selectObject(parentId, false, false);
					const childId = parseInt(groupKey[1]);
					selectObject(parentId, false, false, childId);
				}
			}
		}

		// Update text in state as user typing
		if (this.currentPageId === selectedPageId) {
			this.updateTextContent(true);
		}
	}

	/**
	 * Register event on jquery this.editor element
	 * @param editor { any }
	 * @param eventName { string }
	 * @param callback { Function }
	 * @param before { boolean }
	 */
	protected registerEvent(editor: any, eventName: string, callback: Function, before: boolean = false) {
		if (!editor || !eventName || !callback) {
			return;
		}

		this.listeningEvents.push(eventName);
		// pass this instance and callback as event data.
		editor.events.on(eventName, callback, before);
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		if (this.editor && this.properties.editOn) {
			this.editor.edit.on();
			this.properties.setTextEditorInstance(this.editor);
		}
		return v("div", { id: `${this.properties.shapeId}`, innerHTML: `${this.properties.content}` });
	}
}

export default FroalaEditorWrapper;
